import React from 'react';
import { CheckCircle } from 'lucide-react';
import { ACCENTS, Icon, sectionBg } from './theme';

export const DarkCards = ({ section, accent }) => {
  const t = ACCENTS[accent];
  return (
    <section className="py-20 px-6 lg:px-12 bg-gray-900 text-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className={`${t.labelDark} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
          <h2 className="text-3xl lg:text-4xl font-bold mt-2 mb-4">{section.heading}</h2>
          <p className="text-gray-400 max-w-3xl mx-auto">{section.sub}</p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {section.cards.map((card) => (
            <div key={card.title} className="bg-white/5 backdrop-blur p-8 rounded-2xl border border-white/10">
              <Icon name={card.icon} className={`w-12 h-12 ${t.darkIcon} mb-4`} />
              <h3 className="text-xl font-bold mb-3">{card.title}</h3>
              <p className="text-gray-400 mb-4">{card.desc}</p>
              <div className={`${t.darkBadge} px-4 py-2 rounded-lg inline-block`}>
                <span className={`${t.darkBadgeText} font-bold`}>{card.badge}</span>
              </div>
            </div>
          ))}
        </div>

        {section.extraChips && (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {section.extraChips.map((item) => (
              <div key={item} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl border border-white/10">
                <CheckCircle className={`w-5 h-5 ${t.darkCheck} flex-shrink-0`} />
                <span className="text-gray-300">{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const ListGroups = ({ section, accent }) => {
  const t = ACCENTS[accent];
  return (
    <section className={sectionBg(section.bg)}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">{section.heading}</h2>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {section.groups.map((group) => (
            <div key={group.title} className={`bg-gradient-to-br ${t.groupPanel} p-8 rounded-2xl`}>
              <h3 className="text-xl font-bold text-gray-900 mb-5">{group.title}</h3>
              <ul className="space-y-3">
                {group.items.map((item) => (
                  <li key={item} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className={`w-5 h-5 ${t.check} flex-shrink-0`} />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const IconCards = ({ section, accent }) => {
  const t = ACCENTS[accent];
  return (
    <section className={sectionBg(section.bg)}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">{section.heading}</h2>
          <p className="text-gray-600 max-w-3xl mx-auto">{section.sub}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {section.cards.map((item) => (
            <div key={item.title} className="bg-white p-6 rounded-2xl shadow-sm text-center">
              <div className={`${t.iconBox} w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4`}>
                <Icon name={item.icon} className={`w-8 h-8 ${t.icon}`} />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.desc}</p>
            </div>
          ))}
        </div>

        {section.bigStats && (
          <div className="mt-12 bg-white p-8 rounded-3xl shadow-sm">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              {section.bigStats.map((stat) => (
                <div key={stat.label}>
                  <p className={`text-4xl font-bold ${t.stat}`}>{stat.value}</p>
                  <p className="text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export const ItemGrid = ({ section, accent }) => {
  const t = ACCENTS[accent];
  return (
    <section className={sectionBg(section.bg)}>
      <div className="max-w-6xl mx-auto">
        <div className={`text-center ${section.sub ? 'mb-16' : 'mb-12'}`}>
          <span className={`${t.label} font-semibold uppercase tracking-wide text-sm`}>{section.label}</span>
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2 mb-4">{section.heading}</h2>
          {section.sub && <p className="text-lg text-gray-600 max-w-3xl mx-auto">{section.sub}</p>}
        </div>

        {section.variant === 'gradient' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {section.items.map((item) => (
              <div key={item} className={`bg-gradient-to-br ${t.groupPanel} p-6 rounded-2xl flex items-center gap-3`}>
                <Icon name={section.icon} className={`w-6 h-6 ${t.check} flex-shrink-0`} />
                <span className="font-semibold text-gray-900">{item}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {section.items.map((item) => (
              <div key={item} className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex items-center gap-3">
                <Icon name={section.icon} className={`w-5 h-5 ${t.check} flex-shrink-0`} />
                <span className="text-gray-800 font-medium">{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
